/**
 * VideoLibraryPicker Component
 *
 * A dialog for selecting videos from the coach video library.
 * Used for inserting videos into messages, annotations, etc.
 */

import { useState, useEffect } from "react";
import {
  useCoachVideoLibrary,
  VIDEO_CATEGORIES,
  type CoachVideo,
  type VideoCategory,
} from "@/hooks/use-coach-video-library";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import {
  Video,
  Search,
  Loader2,
  Clock,
  Check,
  FileVideo,
} from "lucide-react";

interface VideoLibraryPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (video: CoachVideo) => void;
  title?: string;
  description?: string;
}

export function VideoLibraryPicker({
  open,
  onOpenChange,
  onSelect,
  title = "Select Video",
  description = "Choose a video from your library",
}: VideoLibraryPickerProps) {
  const { videos, loading, fetchVideos, getVideoUrl } = useCoachVideoLibrary();

  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<VideoCategory | "all">("all");
  const [selectedVideo, setSelectedVideo] = useState<CoachVideo | null>(null);

  // Apply filters
  useEffect(() => {
    if (!open) return;

    const filters: any = {};
    if (categoryFilter !== "all") {
      filters.category = categoryFilter;
    }
    if (searchQuery.trim()) {
      filters.search = searchQuery.trim();
    }
    fetchVideos(filters);
  }, [open, categoryFilter, searchQuery, fetchVideos]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedVideo(null);
      setSearchQuery("");
      setCategoryFilter("all");
    }
  }, [open]);

  // Format duration
  const formatDuration = (seconds: number | null) => {
    if (!seconds) return "--:--";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Get category label
  const getCategoryLabel = (category: VideoCategory | null) => {
    return VIDEO_CATEGORIES.find((c) => c.value === category)?.label || "Uncategorized";
  };

  const handleConfirm = () => {
    if (selectedVideo) {
      onSelect(selectedVideo);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {/* Search and Filter */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search videos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          <Select
            value={categoryFilter}
            onValueChange={(v) => setCategoryFilter(v as VideoCategory | "all")}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {VIDEO_CATEGORIES.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Video List */}
        <ScrollArea className="flex-1 -mx-6 px-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : videos.length === 0 ? (
            <div className="text-center py-12">
              <FileVideo className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No videos found</p>
              <p className="text-sm text-muted-foreground">
                Try adjusting your search or filters
              </p>
            </div>
          ) : (
            <div className="space-y-2 py-2">
              {videos.map((video) => (
                <button
                  key={video.id}
                  onClick={() => setSelectedVideo(video)}
                  className={cn(
                    "w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors",
                    selectedVideo?.id === video.id
                      ? "bg-primary/10 border-2 border-primary"
                      : "hover:bg-muted border-2 border-transparent"
                  )}
                >
                  {/* Thumbnail */}
                  <div className="w-24 aspect-video bg-muted rounded relative shrink-0">
                    {video.thumbnail_path ? (
                      <img
                        src={video.thumbnail_path}
                        alt={video.title}
                        className="w-full h-full object-cover rounded"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Video className="h-6 w-6 text-muted-foreground" />
                      </div>
                    )}
                    {video.duration_seconds && (
                      <div className="absolute bottom-1 right-1 px-1 py-0.5 bg-black/70 text-white text-xs rounded">
                        {formatDuration(video.duration_seconds)}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium truncate">{video.title}</h4>
                    <p className="text-sm text-muted-foreground truncate">
                      {video.description || "No description"}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-xs">
                        {getCategoryLabel(video.category)}
                      </Badge>
                      {video.tags && video.tags.length > 0 && (
                        <span className="text-xs text-muted-foreground">
                          {video.tags.slice(0, 2).join(", ")}
                          {video.tags.length > 2 && `...`}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Selection indicator */}
                  {selectedVideo?.id === video.id && (
                    <div className="shrink-0 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                      <Check className="h-4 w-4 text-primary-foreground" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t">
          <p className="text-sm text-muted-foreground">
            {videos.length} video{videos.length !== 1 ? "s" : ""} available
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={!selectedVideo}>
              Select Video
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default VideoLibraryPicker;
